import React, { useState } from "react";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { auth, db, googleProvider } from "./firebase";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { ref, get } from "firebase/database";
import { useAuth } from "./AuthContext";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaLock, FaEnvelope, FaShieldAlt } from "react-icons/fa";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setRole, setEnrolledCourse } = useAuth();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate email format
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      setLoading(false);
      return;
    }

    try {
      console.log("Attempting to sign in with email:", email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log("Firebase auth successful:", userCredential.user.uid);

      const user = userCredential.user;
      console.log("User object:", user);

      // Helper
      function normalizeEmail(email) {
        return email.trim().toLowerCase().replace(/\./g, ',');
      }

      // First, check if user exists in any course's students list
      const coursesRef = ref(db, "courses");
      const coursesSnapshot = await get(coursesRef);
      if (coursesSnapshot.exists()) {
        const courses = coursesSnapshot.val();
        for (const [courseId, courseData] of Object.entries(courses)) {
          if (courseData.students) {
            const student = Object.values(courseData.students).find(
              (s) => s.email === user.email
            );
            if (student) {
              setRole("student");
              setEnrolledCourse(courseId);
              navigate("/dashboard");
              setLoading(false);
              return;
            }
          }
        }
      }

      // Let AuthContext handle enrollment detection
      // Just set the role and let the user navigate based on AuthContext state
      setRole("student");

      // Wait a moment for AuthContext to update, then navigate based on enrollment status
      setTimeout(() => {
        // Check if user has a pending registration
        const emailKey = normalizeEmail(user.email);
        const studentRef = ref(db, `students/${emailKey}`);
        get(studentRef).then((studentSnap) => {
          if (studentSnap.exists()) {
            const studentData = studentSnap.val();
            console.log("Found student data:", studentData);

            if (studentData.status === "pending") {
              console.log("User has pending registration, redirecting to complete-registration");
              navigate("/complete-registration", { replace: true });
            } else if (studentData.course) {
              console.log("User has course, redirecting to dashboard");
              navigate("/dashboard");
            } else {
              console.log("User has no course, redirecting to complete-registration");
              navigate("/complete-registration", { replace: true });
            }
          } else {
            console.log("No student record found, redirecting to complete-registration");
            navigate("/complete-registration", { replace: true });
          }
        });
      }, 100);
    } catch (err) {
      console.error("Login error details:", {
        code: err.code,
        message: err.message,
        fullError: err
      });

      switch (err.code) {
        case "auth/invalid-email":
          setError("Invalid email address format.");
          break;
        case "auth/user-disabled":
          setError("Your account has been disabled. Please contact support.");
          break;
        case "auth/user-not-found":
          setError("No account found with this email address.");
          break;
        case "auth/wrong-password":
          setError("Incorrect password. Please try again.");
          break;
        case "auth/too-many-requests":
          setError("Too many failed attempts. Please try again later or reset your password.");
          break;
        case "auth/network-request-failed":
          setError("Network error. Please check your internet connection.");
          break;
        default:
          setError(`Login failed: ${err.message}`);
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value && !validateEmail(value)) {
      setError("Please enter a valid email address.");
    } else {
      setError("");
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      console.log("Google sign-in successful:", user.uid);

      // Helper
      function normalizeEmail(email) {
        return email.trim().toLowerCase().replace(/\./g, ',');
      }

      // First, check if user exists in any course's students list
      const coursesRef = ref(db, "courses");
      const coursesSnapshot = await get(coursesRef);
      if (coursesSnapshot.exists()) {
        const courses = coursesSnapshot.val();
        for (const [courseId, courseData] of Object.entries(courses)) {
          if (courseData.students) {
            const student = Object.values(courseData.students).find(
              (s) => s.email === user.email
            );
            if (student) {
              setRole("student");
              setEnrolledCourse(courseId);
              navigate("/dashboard");
              setLoading(false);
              return;
            }
          }
        }
      }

      // Let AuthContext handle enrollment detection
      setRole("student");

      // Wait a moment for AuthContext to update, then navigate based on enrollment status
      setTimeout(() => {
        const emailKey = normalizeEmail(user.email);
        const studentRef = ref(db, `students/${emailKey}`);
        get(studentRef).then((studentSnap) => {
          if (studentSnap.exists()) {
            const studentData = studentSnap.val();
            console.log("Found student data:", studentData);

            if (studentData.status === "pending") {
              console.log("User has pending registration, redirecting to complete-registration");
              navigate("/complete-registration", { replace: true });
            } else if (studentData.course) {
              console.log("User has course, redirecting to dashboard");
              navigate("/dashboard");
            } else {
              console.log("User has no course, redirecting to complete-registration");
              navigate("/complete-registration", { replace: true });
            }
          } else {
            console.log("No student record found, redirecting to complete-registration");
            navigate("/complete-registration", { replace: true });
          }
        });
      }, 100);
    } catch (err) {
      console.error("Google sign-in error:", err);

      switch (err.code) {
        case "auth/popup-closed-by-user":
          setError("Sign-in cancelled. Please try again.");
          break;
        case "auth/popup-blocked":
          setError("Pop-up blocked. Please enable pop-ups for this site.");
          break;
        case "auth/account-exists-with-different-credential":
          setError("An account already exists with the same email address.");
          break;
        case "auth/network-request-failed":
          setError("Network error. Please check your internet connection.");
          break;
        default:
          setError(`Sign-in failed: ${err.message}`);
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-sans bg-gray-900 text-white min-h-screen">
      <Navbar />

      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-sky-900/30 -z-10"></div>
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-center gap-12">
          {/* Visual Element */}
          <div className="hidden lg:flex lg:w-1/2 justify-center items-center">
            <div className="relative w-full max-w-lg">
              <div className="absolute -top-10 -left-10 w-32 h-32 bg-blue-600 rounded-full filter blur-3xl opacity-30"></div>
              <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-sky-600 rounded-full filter blur-3xl opacity-30"></div>
              <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-12 shadow-2xl backdrop-blur-sm">
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-sky-600 rounded-full flex items-center justify-center mb-6">
                    <FaShieldAlt className="text-4xl text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">Secure Login</h3>
                  <p className="text-gray-400">
                    Access your personalized learning dashboard with industry-standard encryption
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="w-full lg:w-1/2 max-w-md">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-8 shadow-2xl backdrop-blur-sm">
              <h2 className="text-3xl font-bold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-sky-300">
                Welcome Back
              </h2>

              {error && (
                <div className="mb-6 p-4 bg-red-900/50 text-red-300 rounded-lg border border-red-700 text-sm text-center">
                  {error}
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-6">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-400 mb-2">Email</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="text-gray-500" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      onBlur={() => {
                        if (email && !validateEmail(email)) {
                          setError("Please enter a valid email address.");
                        }
                      }}
                      placeholder="Enter your email"
                      className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-white placeholder-gray-500"
                      required
                    />
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-sm font-medium text-gray-400 mb-2">Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-gray-500" />
                    </div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition text-white placeholder-gray-500"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-8 py-3 rounded-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white font-medium transition-all duration-300 shadow-lg hover:shadow-blue-500/30 flex justify-center items-center"
                >
                  {loading ? (
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : null}
                  {loading ? "Authenticating..." : "Login"}
                </button>
              </form>

              <div className="mt-6 flex items-center">
                <div className="flex-1 border-t border-gray-700"></div>
                <span className="px-4 text-sm text-gray-400">or</span>
                <div className="flex-1 border-t border-gray-700"></div>
              </div>

              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full mt-4 px-8 py-3 rounded-full bg-white hover:bg-gray-100 text-gray-900 font-medium transition-all duration-300 shadow-lg hover:shadow-gray-500/30 flex justify-center items-center"
              >
                <svg className="mr-3" width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" fill="#34A853"/>
                  <path d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
                </svg>
                {loading ? "Signing in..." : "Sign in with Google"}
              </button>

              <div className="mt-6 text-center text-sm text-gray-400">
                <Link
                  to="/forgot-password"
                  className="hover:text-blue-400 hover:underline transition"
                >
                  Forgot password?
                </Link>
                <p className="mt-4">
                  Don't have an account?{" "}
                  <Link
                    to="/register"
                    className="text-blue-400 font-medium hover:text-blue-300 hover:underline transition"
                  >
                    Create account
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Login;